import { Injectable, inject } from '@angular/core';
import { CookieConsentService } from '../../core/privacy/cookie-consent.service';
import { isPrivateTicketPage } from '../../core/privacy/recovery-link';
import { environment } from '../../../environments/environment';

const BLOCKED_KEYS = [
  'email',
  'phone',
  'telefone',
  'cpf',
  'cnpj',
  'name',
  'nome',
  'fullname',
  'full_name',
  'firstname',
  'first_name',
  'lastname',
  'last_name',
  'document',
  'documento',
  'address',
  'endereco',
  'token',
  'password',
  'senha',
  'buyerEmail',
  'customerEmail',
  'creditCard',
  'cardNumber',
  'cardHash',
  'cvv',
  'cvc',
  'authorization',
  'accessToken',
  'publicToken',
  'secret',
  'holderName',
  'ticketAccessToken',
  'sessionToken',
  'recoveryToken',
];

@Injectable({ providedIn: 'root' })
export class DataLayerService {
  private readonly consent = inject(CookieConsentService);
  canTrack(): boolean {
    return (
      this.consent.allows('analytics') &&
      (typeof window === 'undefined' ||
        !isPrivateTicketPage(window.location.pathname))
    );
  }
  pushEvent(eventName: string, params?: Record<string, any>): void {
    if (!this.canTrack()) return;
    const ga = environment.ga;
    if (!ga?.enabled || !ga.id) return;
    const win = typeof window !== 'undefined' ? (window as any) : null;
    if (!win) return;

    this.ensureDataLayer(win);
    this.ensureGtag(win);

    const payload = this.sanitize(eventName, params);
    if (!payload) return;
    try {
      const { event, ...eventParams } = payload;
      win.gtag('event', event, eventParams);
    } catch {
      // fail silently to avoid quebrar UI
    }
  }

  private ensureDataLayer(win: any): void {
    win.dataLayer = win.dataLayer || [];
  }

  private ensureGtag(win: any): void {
    if (typeof win.gtag === 'function') return;
    win.gtag = (...args: unknown[]) => {
      win.dataLayer.push(args);
    };
  }

  private sanitize(
    eventName: string,
    params?: Record<string, any>
  ): Record<string, any> | null {
    const sanitized: Record<string, any> = { event: eventName };
    if (!params) return sanitized;

    Object.entries(params).forEach(([key, value]) => {
      const sanitizedValue = this.sanitizeValue(key, value);
      if (sanitizedValue !== undefined) {
        sanitized[key] = sanitizedValue;
      }
    });

    return sanitized;
  }

  private sanitizeValue(key: string, value: any): any {
    if (this.shouldBlockKey(key)) return undefined;
    if (value === null || value === undefined) return undefined;

    const valueType = typeof value;
    if (
      valueType === 'string' ||
      valueType === 'number' ||
      valueType === 'boolean'
    ) {
      return value;
    }

    if (Array.isArray(value)) {
      const sanitizedItems = value
        .map((item) => this.sanitizeArrayItem(item))
        .filter((item) => item !== undefined);
      return sanitizedItems.length ? sanitizedItems : undefined;
    }

    if (valueType === 'object') {
      const sanitizedObject = this.sanitizeObject(value);
      return Object.keys(sanitizedObject).length ? sanitizedObject : undefined;
    }

    return String(value);
  }

  private shouldBlockKey(key: string): boolean {
    const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
    return BLOCKED_KEYS.some(
      (blocked) =>
        normalizedKey === blocked.toLowerCase().replace(/[^a-z0-9]/g, '')
    );
  }

  private sanitizeArrayItem(value: any): any {
    if (value === null || value === undefined) return undefined;

    const valueType = typeof value;
    if (
      valueType === 'string' ||
      valueType === 'number' ||
      valueType === 'boolean'
    ) {
      return value;
    }

    if (Array.isArray(value)) {
      const sanitizedItems = value
        .map((item) => this.sanitizeArrayItem(item))
        .filter((item) => item !== undefined);
      return sanitizedItems.length ? sanitizedItems : undefined;
    }

    if (valueType === 'object') {
      const sanitizedObject = this.sanitizeObject(value);
      return Object.keys(sanitizedObject).length ? sanitizedObject : undefined;
    }

    return String(value);
  }

  private sanitizeObject(value: Record<string, any>): Record<string, any> {
    const sanitized: Record<string, any> = {};

    Object.entries(value).forEach(([nestedKey, nestedValue]) => {
      const sanitizedValue = this.sanitizeValue(nestedKey, nestedValue);
      if (sanitizedValue !== undefined) {
        sanitized[nestedKey] = sanitizedValue;
      }
    });

    return sanitized;
  }
}
